import React, { useEffect, useState } from "react";
import {
  adminSubs, adminSubVerify, adminSubReject, adminUsers, adminSessions,
  adminAllTradesPdfUrl, adminUserSetStatus, adminCoupons, adminCouponCreate,
  adminCouponUpdate, adminCouponDelete, getToken,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Shield, Loader2, CheckCircle2, XCircle, RefreshCw, Users, Clock,
  Download, Ticket, Plus, Trash2, Power, PowerOff,
} from "lucide-react";

export default function AdminPanel({ compact = false }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("subs");
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, se, c] = await Promise.all([
        adminSubs(), adminUsers(), adminSessions(),
        adminCoupons().catch(() => ({ coupons: [] })),
      ]);
      setSubs(s.subscriptions || []);
      setUsers(u.users || []);
      setSessions(se.sessions || []);
      setCoupons(c.coupons || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!user?.is_admin) {
    return (
      <div data-testid="admin-denied" className={compact ? "py-16 text-white flex items-center justify-center" : "min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"}>
        <div className="text-center max-w-md">
          <Shield className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
          <h2 className="font-display text-2xl font-bold">Admin Access Required</h2>
          <p className="text-zinc-400 mt-2 text-sm">Your account ({user?.email}) is not in the admin list.</p>
        </div>
      </div>
    );
  }

  const verify = async (uid) => {
    setBusy(uid);
    try { await adminSubVerify(uid); toast.success("Subscription verified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
    finally { setBusy(null); }
  };
  const reject = async (uid) => {
    setBusy(uid);
    try { await adminSubReject(uid, "Payment could not be verified"); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };

  const toggleUserStatus = async (u) => {
    const next = u.status === "disabled" ? "active" : "disabled";
    if (!confirm(`${next === "disabled" ? "Disable" : "Re-enable"} ${u.email}? ${next === "disabled" ? "This will log them out immediately." : ""}`)) return;
    setBusy(u.user_id);
    try {
      await adminUserSetStatus(u.user_id, next);
      toast.success(`User ${next === "disabled" ? "disabled" : "re-enabled"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed"); }
    finally { setBusy(null); }
  };

  const createCoupon = async (payload) => {
    try {
      await adminCouponCreate(payload);
      toast.success(`Coupon ${payload.code} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const updateCoupon = async (code, payload) => {
    try { await adminCouponUpdate(code, payload); toast.success("Coupon updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const deleteCoupon = async (code) => {
    if (!confirm(`Delete coupon ${code}?`)) return;
    try { await adminCouponDelete(code); toast.success("Coupon deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const downloadAllPdf = async () => {
    try {
      const token = getToken();
      const r = await fetch(adminAllTradesPdfUrl(), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nava-intra-ALL-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("All-trades PDF downloaded");
    } catch { toast.error("PDF download failed"); }
  };

  return (
    <div data-testid="admin-panel" className={compact ? "text-white" : "min-h-screen bg-[#0A0A0A] text-white p-8"}>
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F472B6]/15 border border-[#F472B6]/30 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#F472B6]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold leading-tight">Admin Console</h1>
            <p className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider">Owner-only · Subs · Users · Coupons · Trades PDF</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="admin-tab-subs" onClick={() => setTab("subs")} className={pill(tab === "subs")}>Subscriptions</button>
          <button data-testid="admin-tab-users" onClick={() => setTab("users")} className={pill(tab === "users")}>Users / Logins</button>
          <button data-testid="admin-tab-coupons" onClick={() => setTab("coupons")} className={pill(tab === "coupons")}>Coupons</button>
          <button data-testid="admin-refresh" onClick={load} className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-full text-xs flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button data-testid="admin-all-pdf" onClick={downloadAllPdf} className="px-3 py-1.5 bg-[#FFCC00]/10 hover:bg-[#FFCC00]/20 border border-[#FFCC00]/30 text-[#FFCC00] rounded-full text-xs flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> All Trades PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      ) : tab === "subs" ? (
        <SubsTab subs={subs} onVerify={verify} onReject={reject} busy={busy} />
      ) : tab === "users" ? (
        <UsersTab users={users} sessions={sessions} onToggleStatus={toggleUserStatus} busy={busy} />
      ) : (
        <CouponsTab coupons={coupons} onCreate={createCoupon} onUpdate={updateCoupon} onDelete={deleteCoupon} />
      )}
    </div>
  );
}

const pill = (active) => `px-4 py-1.5 rounded-full text-sm font-medium ${active ? "bg-[#F472B6]/15 text-[#F472B6] border border-[#F472B6]/30" : "text-zinc-500 hover:text-white"}`;

function SubsTab({ subs, onVerify, onReject, busy }) {
  const pending = subs.filter(s => s.status === "pending");
  const active = subs.filter(s => s.status === "active");
  const other = subs.filter(s => !["pending", "active"].includes(s.status));
  return (
    <div className="space-y-6">
      <Section title={`Pending review (${pending.length})`} data-testid="admin-subs-pending">
        {pending.length === 0 ? <Empty text="No pending subscriptions" /> : pending.map(s => <SubRow key={s.user_id} s={s} onVerify={onVerify} onReject={onReject} busy={busy} />)}
      </Section>
      <Section title={`Active (${active.length})`}>
        {active.length === 0 ? <Empty text="No active subscriptions yet" /> : active.map(s => <SubRow key={s.user_id} s={s} onVerify={onVerify} onReject={onReject} busy={busy} readonly />)}
      </Section>
      {other.length > 0 && (
        <Section title={`Other (${other.length})`}>
          {other.map(s => <SubRow key={s.user_id} s={s} onVerify={onVerify} onReject={onReject} busy={busy} readonly />)}
        </Section>
      )}
    </div>
  );
}

function SubRow({ s, onVerify, onReject, busy, readonly }) {
  const isPending = s.status === "pending";
  return (
    <div data-testid={`admin-sub-${s.user_id}`} className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-4 flex flex-wrap items-start gap-4">
      <div className="flex-1 min-w-[240px]">
        <div className="text-sm font-medium">{s.name} <span className="text-zinc-500 font-mono text-xs">· {s.email}</span></div>
        <div className="text-[11px] text-zinc-500 font-mono mt-1">
          {s.coupon_code && <span className="mr-3">coupon: <span className="text-[#00E676]">{s.coupon_code}</span></span>}
          {s.tx_hash && <span className="mr-3">tx: {s.tx_hash.slice(0, 20)}…</span>}
          submitted: {s.submitted_at?.slice(0, 19).replace("T", " ")}
        </div>
        {s.admin_note && <div className="text-[11px] text-zinc-600 mt-1 italic">note: {s.admin_note}</div>}
      </div>
      <div className="flex items-center gap-2">
        <span data-testid={`admin-sub-status-${s.user_id}`} className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wider ${
          s.status === "active" ? "bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30"
            : s.status === "pending" ? "bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30"
              : "bg-zinc-800 text-zinc-500 border border-zinc-700"
        }`}>{s.status}</span>
        {!readonly && isPending && (
          <>
            <button data-testid={`admin-sub-verify-${s.user_id}`} disabled={busy === s.user_id} onClick={() => onVerify(s.user_id)} className="px-3 py-1.5 bg-[#00E676]/10 hover:bg-[#00E676]/20 border border-[#00E676]/30 text-[#00E676] rounded-sm text-xs flex items-center gap-1 disabled:opacity-40">
              <CheckCircle2 className="w-3.5 h-3.5" /> Verify
            </button>
            <button data-testid={`admin-sub-reject-${s.user_id}`} disabled={busy === s.user_id} onClick={() => onReject(s.user_id)} className="px-3 py-1.5 bg-[#FF3B30]/10 hover:bg-[#FF3B30]/20 border border-[#FF3B30]/30 text-[#FF3B30] rounded-sm text-xs flex items-center gap-1 disabled:opacity-40">
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function UsersTab({ users, sessions, onToggleStatus, busy }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Section title={`Users (${users.length})`} icon={<Users className="w-4 h-4 text-zinc-500" />}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {users.map(u => {
            const disabled = u.status === "disabled";
            return (
              <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="p-3 bg-black/40 border border-zinc-900 rounded-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  {u.picture && <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />}
                  <div className="text-sm">{u.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                  <span data-testid={`admin-user-status-${u.user_id}`} className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${disabled ? "bg-red-500/15 text-red-400 border border-red-500/30" : "bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30"}`}>
                    {disabled ? "disabled" : "active"}
                  </span>
                  {u.subscription?.status && (
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                      u.subscription.status === "active" ? "bg-[#00E676]/15 text-[#00E676]"
                        : u.subscription.status === "pending" ? "bg-[#FFCC00]/15 text-[#FFCC00]"
                          : "bg-zinc-800 text-zinc-500"
                    }`}>sub: {u.subscription.status}</span>
                  )}
                  <button
                    data-testid={`admin-user-toggle-${u.user_id}`}
                    onClick={() => onToggleStatus(u)}
                    disabled={busy === u.user_id}
                    className={`ml-auto px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border flex items-center gap-1 ${disabled ? "text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" : "text-red-400 border-red-500/30 hover:bg-red-500/10"}`}
                  >
                    {disabled ? <Power className="w-3 h-3" /> : <PowerOff className="w-3 h-3" />}
                    {disabled ? "Enable" : "Disable"}
                  </button>
                </div>
                <div className="text-[10px] text-zinc-600 font-mono mt-1">joined {u.created_at?.slice(0, 10)}{u.last_login ? ` · last login ${u.last_login?.slice(0, 19).replace("T", " ")}` : ""}</div>
              </div>
            );
          })}
        </div>
      </Section>
      <Section title={`Recent Sessions (${sessions.length})`} icon={<Clock className="w-4 h-4 text-zinc-500" />}>
        <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
          {sessions.slice(0, 100).map((s, i) => (
            <div key={i} className="p-2 bg-black/30 border border-zinc-900 rounded-sm text-[11px] font-mono flex items-center gap-3">
              <span className="text-zinc-500">{s.created_at?.slice(0, 19).replace("T", " ")}</span>
              <span className="text-zinc-400 flex-1 truncate">{s.user_id}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function CouponsTab({ coupons, onCreate, onUpdate, onDelete }) {
  const [form, setForm] = useState({ code: "", duration_days: "", max_uses: "", expires_at: "", label: "", active: true });
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim(),
      duration_days: form.duration_days ? Number(form.duration_days) : null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      expires_at: form.expires_at || null,
      label: form.label,
      active: form.active,
    };
    if (!payload.code) return toast.error("Code required");
    await onCreate(payload);
    setForm({ code: "", duration_days: "", max_uses: "", expires_at: "", label: "", active: true });
  };
  return (
    <div className="space-y-6">
      <Section title="Create new coupon" icon={<Ticket className="w-4 h-4 text-[#F472B6]" />}>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-2" data-testid="admin-coupon-form">
          <input data-testid="admin-coupon-code" placeholder="CODE" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm font-mono" />
          <input data-testid="admin-coupon-days" placeholder="Duration days" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value.replace(/[^\d]/g, "") })} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="admin-coupon-maxuses" placeholder="Max uses" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value.replace(/[^\d]/g, "") })} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="admin-coupon-expiry" type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ""} onChange={e => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="admin-coupon-label" placeholder="Label" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="admin-coupon-submit" type="submit" className="px-4 py-2 bg-[#F472B6] text-black rounded-lg text-sm font-semibold flex items-center gap-2 justify-center hover:brightness-110">
            <Plus className="w-4 h-4" /> Mint
          </button>
        </form>
        <div className="text-[10px] text-zinc-500 font-mono mt-2">
          Leave duration blank (or 0) for permanent access. Leave max uses blank for unlimited. Expiry date is optional.
        </div>
      </Section>
      <Section title={`All coupons (${coupons.length})`}>
        {coupons.length === 0 ? <Empty text="No coupons yet" /> : (
          <div className="space-y-2">
            {coupons.map(c => <CouponRow key={c.code} c={c} onUpdate={onUpdate} onDelete={onDelete} />)}
          </div>
        )}
      </Section>
    </div>
  );
}

function CouponRow({ c, onUpdate, onDelete }) {
  const exhausted = c.max_uses != null && c.uses_count >= c.max_uses;
  const expired = c.expires_at && new Date(c.expires_at) < new Date();
  const disabled = !c.active || exhausted || expired;
  return (
    <div data-testid={`admin-coupon-${c.code}`} className="p-3 bg-black/40 border border-zinc-900 rounded-sm flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold text-white">{c.code}</span>
          <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${disabled ? "bg-red-500/15 text-red-400 border border-red-500/30" : "bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30"}`}>
            {expired ? "expired" : exhausted ? "exhausted" : c.active ? "active" : "inactive"}
          </span>
          {c.label && <span className="text-[11px] text-zinc-500">{c.label}</span>}
        </div>
        <div className="text-[11px] text-zinc-500 font-mono mt-1">
          {c.duration_days ? `${c.duration_days} days` : "permanent"} · uses {c.uses_count}{c.max_uses != null ? `/${c.max_uses}` : "/∞"} {c.expires_at ? ` · exp ${c.expires_at.slice(0, 10)}` : ""}
        </div>
      </div>
      <button
        data-testid={`admin-coupon-toggle-${c.code}`}
        onClick={() => onUpdate(c.code, { code: c.code, duration_days: c.duration_days, max_uses: c.max_uses, expires_at: c.expires_at, label: c.label, active: !c.active })}
        className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-zinc-800 text-zinc-300 hover:bg-zinc-900"
      >
        {c.active ? "Deactivate" : "Activate"}
      </button>
      <button
        data-testid={`admin-coupon-delete-${c.code}`}
        onClick={() => onDelete(c.code)}
        className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/10"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function Section({ title, icon, children, ...rest }) {
  return (
    <div {...rest} className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-5">
      <h3 className="font-display text-sm font-semibold mb-4 flex items-center gap-2">{icon} {title}</h3>
      {children}
    </div>
  );
}
function Empty({ text }) { return <div className="text-sm text-zinc-500 py-6 text-center">{text}</div>; }
